import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { Domain } from '../types/outreach';
import { renderCompactLink } from '../utils/formatters';

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};



const TYPE_OPTIONS = [
  'THC',
  'CBD',
  'INV',
  'MED',
  'THC, CBD',
  'THC, INV',
  'CBD, INV',
  'THC, MED',
  'CBD, MED',
  'INV, MED',
  'THC, CBD, INV',
  'THC, CBD, MED',
  'THC, INV, MED',
  'CBD, INV, MED',
  'THC, CBD, INV, MED',
];

// Tag to Type mapping based on user requirements
const TAG_TO_TYPE_MAP: Record<string, string> = {
  'Association': 'THC',
  'Banking': 'INV',
  'Cannabis Industry': 'THC',
  'Cannabis Professional': 'THC',
  'Cannabis School': 'THC',
  'CBD Affiliate Network': 'CBD',
  'Cbd Association': 'CBD',
  'Cbd Broker': 'CBD',
  'CBD Chiropractor': 'CBD',
  'Cbd Consultant': 'CBD',
  'Cbd Cosmetics': 'CBD',
  'CBD Doctor': 'CBD',
  'Cbd Edibles': 'CBD',
  'CBD Events': 'CBD',
  'Cbd Extractor': 'CBD',
  'CBD Franchise': 'CBD',
  'CBD Industry': 'CBD',
  'CBD Manufacturer': 'CBD',
  'Cbd Marketing': 'CBD',
  'Massage Therapist': 'CBD',
  'Cbd Media': 'CBD',
  'CBD Online Store': 'CBD',
  'Cbd Pets': 'CBD',
  'CBD Product': 'CBD',
  'CBD Professional': 'CBD',
  'CBD Shop': 'CBD',
  'Cbd Sport': 'CBD',
  'Cbd Store': 'CBD',
  'Cbd Training': 'CBD',
  'Cbd Vape': 'CBD',
  'CBD Wellness': 'CBD',
  'Cbd Wholesaler': 'CBD',
  'Chiropractor': 'CBD',
  'Construction': 'THC',
  'Consumer Accessories': 'THC',
  'Delivery': 'THC',
  'Dispensary': 'THC',
  'Distributor': 'THC',
  'Doctors': 'MED',
  'Equipment Installation': 'THC',
  'Events': 'THC',
  'Extractor': 'THC',
  'Growers': 'THC',
  'Growers support': 'THC',
  'Hospital & Healthcare': 'MED',
  'Influencer': 'THC',
  'Instagram consumer': 'THC',
  'Insurance': 'INV',
  'Investors': 'INV',
  'Lab': 'THC',
  'Lawyers': 'THC, INV',
  'Manufacturer Support': 'YHC', // Note: YHC might be a typo, keeping as is
  'Marketing': 'THC',
  'Media': 'THC',
  'Online Cbd Shop': 'CBD',
  'Packaging': 'THC',
  'Payment processor': 'INV',
  'Pharmaceutical': 'MED',
  'Real Estate': 'THC',
  'School': 'THC',
  'Security': 'THC',
  'Seeds': 'THC',
  'Software Company': 'THC',
  'Speakers': 'THC',
  'State Government': 'THC',
  'THC Manufacturer': 'THC',
  'THCA Manufacturer': 'THC',
  'Transporter': 'THC',
  'Head Shop': 'CBD',
  'Wealth Managers': 'INV',
  'Wellness Center': 'CBD',
  'Writers': 'THC',
  'CBD solo store': 'CBD',
  'Post liker': 'THC',
  'Journalist': 'THC, CBD',
  'Accountants': 'INV',
  'Psychedelic': 'THC',
  'Fertilizer Distributor': 'THC or CBD', // Special case - will use first tag's type
  'Cannabis Lab': 'THC',
  'Hemp Lab': 'CBD',
  'Hydroponics Store': 'THC or CBD', // Special case
  'Lab support': 'THC',
  'Hemp cultivator': 'CBD',
  'Cannabis Association': 'THC',
  'Cannabis Bank': 'INV',
  'Hemp Bank': 'CBD',
  'THC Cultivator': 'THC',
  'THC Lab': 'THC',
  'Fertilizer Company': 'THC or CBD', // Special case
  'Recover Center': 'MED',
  'Social Justice': 'THC',
  'Nurses': 'CBD, MED',
  'Pharmacists': 'CBD, MED',
  'Budtender': 'THC',
  'Twitter': 'THC',
  'THC Consultant': 'THC',
  'THC Association': 'THC',
  'Consultants': 'THC, CBD', // Note: This overwrites the earlier 'Consultants': 'THC'
  'Hydroponic distributor': 'THC',
};

// Helper function to determine default type from selected tags
const getDefaultTypeFromTags = (selectedTags: { id: number; name: string }[]): string | null => {
  if (!selectedTags || selectedTags.length === 0) {
    return null;
  }

  // Collect all unique types from selected tags
  const typeSet = new Set<string>();
  let hasAmbiguousTags = false;
  
  // First pass: collect clear types (non-ambiguous)
  for (const tag of selectedTags) {
    const tagName = tag.name;
    const type = TAG_TO_TYPE_MAP[tagName];
    
    if (type) {
      // Handle special cases like "THC or CBD" - mark as ambiguous
      if (type === 'THC or CBD') {
        hasAmbiguousTags = true;
        continue;
      }
      
      // Handle multiple types (e.g., "THC, INV")
      if (type.includes(',')) {
        const types = type.split(',').map(t => t.trim());
        types.forEach(t => typeSet.add(t));
      } else {
        typeSet.add(type);
      }
    }
  }
  
  // If we have ambiguous tags and no clear types, default to THC
  if (typeSet.size === 0 && hasAmbiguousTags) {
    typeSet.add('THC');
  }
  
  if (typeSet.size === 0) {
    return null;
  }
  
  // Convert set to array and sort according to TYPE_OPTIONS order (THC, CBD, INV, MED)
  const typeOrder = ['THC', 'CBD', 'INV', 'MED'];
  const types = Array.from(typeSet).sort((a, b) => {
    const indexA = typeOrder.indexOf(a);
    const indexB = typeOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
  
  // Return the appropriate type string
  if (types.length === 1) {
    return types[0];
  } else if (types.length === 2) {
    return `${types[0]}, ${types[1]}`;
  } else if (types.length === 3) {
    return `${types[0]}, ${types[1]}, ${types[2]}`;
  } else {
    return `${types[0]}, ${types[1]}, ${types[2]}, ${types[3]}`;
  }
};

const DomainListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const getConsolidatedTags = (d: any) => {
    const tags: string[] = [];
    if (d.industry_tag_name) tags.push(d.industry_tag_name);
    if (d.industry_tag_two_name) tags.push(d.industry_tag_two_name);
    if (d.industry_tag_three_name) tags.push(d.industry_tag_three_name);
    if (d.industry_tag_four_name) tags.push(d.industry_tag_four_name);
    return tags.length > 0 ? tags.join(', ') : '-';
  };
  
  const [displayCount, setDisplayCount] = useState(50);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollPositionRef = useRef<number>(0);
  const isEnrichingRef = useRef(false);
  
  // State for data
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [industryTags, setIndustryTags] = useState<{ id: number; name: string }[]>([]);
  
  // State for selection
  const [selectedDomains, setSelectedDomains] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // States for real-time enrichment progress
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState(0);
  const [isEnrichComplete, setIsEnrichComplete] = useState(false);
  const [enrichLogs, setEnrichLogs] = useState<{
    domainId: number;
    website: string;
    name: string;
    status: 'pending' | 'processing' | 'done' | 'failed';
    preCount?: number;
    postCount?: number;
    newCount?: number;
  }[]>([]);
  
  // State to track which Autocomplete dropdowns are open (domain ID -> boolean)
  const [openDropdowns, setOpenDropdowns] = useState<Map<number, boolean>>(new Map());
  
  // State for sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Load selected domains from API
  useEffect(() => {
    const loadDomains = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await ApiService.getSelectedDomains(sessionId);
        const loadedDomains = response.selectedDomains || [];
        setDomains(loadedDomains);
        setSelectedDomains(new Set(loadedDomains.map((d: any) => d.id)));
        setSelectAll(loadedDomains.length > 0);
        setError(null);
      } catch (err) {
        console.error('Error loading selected domains:', err);
        let errorMessage = 'Failed to load domains';
        
        if (err instanceof Error) {
          // Check if it's a network/server error
          if (err.message.includes('Failed to fetch') || 
              err.message.includes('NetworkError') ||
              err.message.includes('Network request failed') ||
              err.name === 'TypeError') {
            errorMessage = 'Server not running. Please start the server';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadDomains();
  }, [sessionId]);

  // Removed auto-start enrichment useEffect as per user request to prevent re-running on back/forward navigation.

  useEffect(() => {
    const fetchIndustryTags = async () => {
      try {
        const response = await ApiService.getOutreachIndustryTags();
        const tagList = response && Array.isArray(response.industryTags) ? response.industryTags : [];
        setIndustryTags(tagList);
      } catch (err) {
        console.error('Error fetching outreach industry tags:', err);
      }
    };

    fetchIndustryTags();
  }, []);

  // Handle load more
  const handleLoadMore = () => {
    if (displayCount < domains.length && !isLoadingMore) {
      setIsLoadingMore(true);
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      setTimeout(() => {
        setDisplayCount(prev => Math.min(prev + 50, domains.length));
        setIsLoadingMore(false);
        
        // Restore scroll position
        if (scrollPositionRef.current > 0) {
          setTimeout(() => {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0;
          }, 50);
        }
      }, 300);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDomains(new Set());
    } else {
      const allIds = domains.map(d => d.id);
      setSelectedDomains(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectDomain = (id: number) => {
    const newSelected = new Set(selectedDomains);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDomains(newSelected);
    setSelectAll(newSelected.size === displayCount);
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort domains
  const sortedDomains = React.useMemo(() => {
    if (!sortField) return domains;
    
    return [...domains].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [domains, sortField, sortDirection]);

  const handleBack = () => {
    navigate(`/outreach/process?session=${sessionId}`);
  };

  const startEnrichment = async (selectedIds: number[]) => {
    setIsEnriching(true);
    isEnrichingRef.current = true;
    setIsEnrichComplete(false);

    // Initialize/preserve logs for selected domains
    const initialLogs = domains
      .filter(d => selectedIds.includes(d.id))
      .map(d => {
        const existingLog = enrichLogs.find(l => l.domainId === d.id);
        if (existingLog && (existingLog.status === 'done' || existingLog.status === 'failed')) {
          return existingLog;
        }
        return {
          domainId: d.id,
          website: d.website,
          name: d.name || d.website,
          status: 'pending' as const,
          preCount: d.deliverable || 0, // Use deliverable count as the baseline
        };
      });
    setEnrichLogs(initialLogs);

    const total = selectedIds.length;
    let completedCount = initialLogs.filter(l => l.status === 'done' || l.status === 'failed').length;
    setEnrichProgress(total > 0 ? Math.round((completedCount / total) * 100) : 0);

    // Helper to update log status
    const updateLog = (id: number, updates: Partial<typeof initialLogs[0]>) => {
      setEnrichLogs(prev => prev.map(log => log.domainId === id ? { ...log, ...updates } : log));
    };

    // Worker function
    const worker = async (id: number) => {
      updateLog(id, { status: 'processing' });
      try {
        const res = await ApiService.enrichSingleData(sessionId!, id);
        updateLog(id, {
          status: 'done',
          preCount: res.preCount,
          postCount: res.postCount,
          newCount: res.newCount,
        });

        // Update the main domains list to reflect new numbers in the table immediately
        setDomains(prev => prev.map(d => 
          d.id === id ? { 
            ...d, 
            deliverable: res.postCount,
            last_ran_gmo: new Date().toISOString()
          } : d
        ));
      } catch (error) {
        console.error(`Error enriching domain ${id}:`, error);
        updateLog(id, { status: 'failed' });
      } finally {
        completedCount++;
        setEnrichProgress(Math.round((completedCount / total) * 100));
      }
    };

    // Process with concurrency limit of 3, ignoring already completed domains
    const queue = initialLogs
      .filter(l => l.status !== 'done' && l.status !== 'failed')
      .map(l => l.domainId);
    const activePromises: Promise<void>[] = [];
    const concurrencyLimit = 3;

    while ((queue.length > 0 || activePromises.length > 0) && isEnrichingRef.current) {
      while (activePromises.length < concurrencyLimit && queue.length > 0 && isEnrichingRef.current) {
        const id = queue.shift()!;
        const p = worker(id).then(() => {
          activePromises.splice(activePromises.indexOf(p), 1);
        });
        activePromises.push(p);
      }
      if (activePromises.length > 0) {
        await Promise.race(activePromises);
      }
    }

    if (isEnrichingRef.current) {
      setIsEnrichComplete(true);
    }
  };

  const handleContinue = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }

    const selectedIds = Array.from(selectedDomains);
    if (selectedIds.length === 0) {
      alert('Please select at least one domain to enrich.');
      return;
    }

    // Start real-time enrichment process
    startEnrichment(selectedIds);
  };

  // Handle remove selected domains
  const handleRemove = async () => {
    if (selectedDomains.size === 0) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Update is_selected = FALSE for selected domains
      const selectedIds = Array.from(selectedDomains);
      await ApiService.updateDomainSelection(sessionId!, selectedIds, false);
      
      
      // Reload domains to reflect changes
      const response = await ApiService.getSelectedDomains(sessionId!);
      setDomains(response.selectedDomains);
      
      // Clear selection
      setSelectedDomains(new Set());
      setSelectAll(false);
      
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error removing domains:', error);
      alert(`Error removing domains: ${error.message || 'Unknown error'}`);
      setIsLoading(false);
    }
  };
  
  // Handle industry tags change (multi-select up to 4 tags)
  const handleStatusChange = async (id: number, selectedTags: { id: number; name: string }[]) => {
    try {
      // Limit to 4 tags and extract IDs
      const tagIds = selectedTags.slice(0, 4).map(tag => tag.id);
      
      // Call new endpoint to update all 4 columns
      await ApiService.updateDomainIndustryTags(sessionId!, id, tagIds);
      
      // Map tags to columns: [tag1, tag2, tag3, tag4] -> industry_tag, industry_tag_two, industry_tag_three, industry_tag_four
      const industryTag = tagIds[0] || undefined;
      const industryTagTwo = tagIds[1] || undefined;
      const industryTagThree = tagIds[2] || undefined;
      const industryTagFour = tagIds[3] || undefined;
      
      const industryTagName = selectedTags[0]?.name || undefined;
      const industryTagTwoName = selectedTags[1]?.name || undefined;
      const industryTagThreeName = selectedTags[2]?.name || undefined;
      const industryTagFourName = selectedTags[3]?.name || undefined;
      
      // Auto-populate type based on selected tags
      const defaultType = getDefaultTypeFromTags(selectedTags);
      if (defaultType) {
        // Always auto-populate type when tags are selected (user can change it manually if needed)
        await handleTypeChange(id, defaultType);
      }
      
      // Update local state immediately without reloading
      setDomains(prev => prev.map(d => 
        d.id === id ? { 
          ...d, 
          industry_tag: industryTag,
          industry_tag_name: industryTagName,
          industry_tag_two: industryTagTwo,
          industry_tag_two_name: industryTagTwoName,
          industry_tag_three: industryTagThree,
          industry_tag_three_name: industryTagThreeName,
          industry_tag_four: industryTagFour,
          industry_tag_four_name: industryTagFourName
        } : d
      ));
      
    } catch (error) {
      console.error('Error updating industry tags:', error);
    }
  };

  const handleTypeChange = async (id: number, type: string) => {
    try {
      await ApiService.updateDomainType(sessionId!, id, type);

      const typeValue = type === '' ? null : type;
      setDomains(prev =>
        prev.map(d => (d.id === id ? { ...d, type_: typeValue } : d))
      );
    } catch (error) {
      console.error('Error updating domain type:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/outreach')} sx={{ mt: 2 }}>
          Back to Upload
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden',
      p: 3
    }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Domain List: {domains.length}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                if (selectedDomains.size === domains.length) {
                  setSelectedDomains(new Set());
                  setSelectAll(false);
                } else {
                  setSelectedDomains(new Set(domains.map(d => d.id)));
                  setSelectAll(true);
                }
              }}
              sx={{
                color: selectedDomains.size === domains.length ? 'success.main' : 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 600,
                border: 'none',
                background: 'none',
                padding: 0,
                '&:hover': {
                  color: selectedDomains.size === domains.length ? 'success.dark' : 'primary.dark',
                }
              }}
            >
              select all in query
            </Link>
            {selectedDomains.size > 0 && (
              <Typography variant="body2" color={selectedDomains.size === domains.length ? "success.main" : "text.secondary"} sx={{ fontWeight: 600 }}>
                ({selectedDomains.size} entries selected)
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={handleBack}
          >
            BACK
          </Button>
          {selectedDomains.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleRemove}
              disabled={isLoading || (isEnriching && !isEnrichComplete)}
            >
              REMOVE ({selectedDomains.size})
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={isEnrichComplete ? () => navigate(`/outreach/validation?session=${sessionId}`, { replace: true }) : handleContinue}
            disabled={isLoading || (isEnriching && !isEnrichComplete)}
            sx={{
              fontWeight: 'bold',
              backgroundColor: isEnrichComplete ? 'success.main' : 'primary.main',
              color: 'white',
              px: 2.5,
              '&:hover': {
                backgroundColor: isEnrichComplete ? 'success.dark' : 'primary.dark',
              }
            }}
          >
            {isLoading 
              ? 'LOADING...' 
              : isEnriching && !isEnrichComplete 
                ? `SCRAPING GMO (${enrichLogs.filter(l => l.status === 'done' || l.status === 'failed').length}/${enrichLogs.length})`
                : isEnrichComplete 
                  ? 'PROCEED TO VALIDATION' 
                  : 'START SCRAPING'}
          </Button>
        </Box>
      </Box>

      {/* Sleek, Premium Glassmorphic Progress Banner */}
      {isEnriching && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(71, 167, 245, 0.08))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(25, 118, 210, 0.12)',
            boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isEnrichComplete ? (
                <CircularProgress size={20} thickness={5} sx={{ color: 'primary.main' }} />
              ) : (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
              )}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a2035' }}>
                  {!isEnrichComplete ? 'GMO Scraper: Sequentially enriching contacts...' : 'GMO Scraper Completed!'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isEnrichComplete
                    ? (
                      <>
                        <Box component="span" sx={{ color: 'primary.main' }}>
                          Processing: {enrichLogs.filter(l => l.status === 'processing').map(l => l.name).join(', ') || 'calculating...'}
                        </Box>
                        • Queued: {enrichLogs.filter(l => l.status === 'pending').length} remaining
                      </>
                    )
                    : `Successfully enriched ${enrichLogs.length} domains. New contacts found: ${enrichLogs.reduce((acc, l) => acc + (l.newCount || 0), 0)}`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.9rem' }}>
                {enrichProgress}% ({enrichLogs.filter(l => l.status === 'done' || l.status === 'failed').length} / {enrichLogs.length})
              </Typography>
              {!isEnrichComplete ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => {
                    setIsEnriching(false);
                    isEnrichingRef.current = false;
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold' }}
                >
                  Pause
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => navigate(`/outreach/validation?session=${sessionId}`, { replace: true })}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', px: 2.5 }}
                >
                  Proceed to Validation
                </Button>
              )}
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={enrichProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundImage: 'linear-gradient(90deg, #1976d2, #47a7f5)',
              },
            }}
          />

          {/* Real-time Activity Log */}
          <Box 
            sx={{ 
              mt: 1, 
              maxHeight: '200px', 
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {enrichLogs.filter(l => l.status !== 'pending').reverse().map((log) => (
                <Box 
                  key={log.domainId}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1.2,
                    borderRadius: '10px',
                    backgroundColor: log.status === 'processing' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid',
                    borderColor: log.status === 'processing' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0,0,0,0.03)',
                    animation: log.status === 'processing' ? 'pulse 2s infinite ease-in-out' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                      '100%': { opacity: 1 },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {log.status === 'processing' ? (
                      <CircularProgress size={12} thickness={6} />
                    ) : log.status === 'done' ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      {log.website}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {log.status === 'done' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          Contacts:
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#1a2035' }}>
                          {log.preCount || 0} → {log.postCount || 0}
                        </Typography>
                        {log.newCount && log.newCount > 0 ? (
                          <Chip 
                            label={`+${log.newCount} new`} 
                            size="small" 
                            color="success" 
                            variant="filled"
                            sx={{ 
                              height: '18px', 
                              fontSize: '0.65rem', 
                              fontWeight: 900,
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              border: 'none'
                            }} 
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                            (no new)
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 800, 
                        textTransform: 'uppercase',
                        fontSize: '0.65rem',
                        color: log.status === 'processing' ? 'primary.main' : log.status === 'done' ? 'success.main' : 'error.main'
                      }}
                    >
                      {log.status}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Table Section */}
      <Paper sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 2
      }}>
        <TableContainer 
          sx={{ 
            maxHeight: 'none',
            overflowY: 'auto',
            overflowX: 'auto'
          }}
        >
          <Table 
            stickyHeader
            sx={{ 
              minWidth: 2000,
              '& .MuiTableCell-root': { 
                padding: '10px 16px',
                whiteSpace: 'nowrap'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 50, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                {/* Match new domains table order: Industry Tag, Type, then company columns */}
                <TableCell sx={{ minWidth: 280, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Tooltip title="Industry Tags (Ctag1, Ctag2, Ctag3, Ctag4)" arrow>
                    <span>Industry Tag</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('type_')}>
                    Type
                    {sortField === 'type_' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 250, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  GMO Scan Progress
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('website')}>
                    Website
                    {sortField === 'website' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                    Company Name
                    {sortField === 'name' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('clean_name')}>
                    Clean Name
                    {sortField === 'clean_name' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('note')}>
                    Notes
                    {sortField === 'note' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>
                    Description
                    {sortField === 'description' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Unsubscribe" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unsubscribe')}>
                      UN
                      {sortField === 'unsubscribe' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Inmailer" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('in_mailer')}>
                      IM
                      {sortField === 'in_mailer' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state_name')}>
                    State
                    {sortField === 'state_name' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('location')}>
                    Location
                    {sortField === 'location' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('country_name')}>
                    Country
                    {sortField === 'country_name' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('company_address')}>
                    Company Address
                    {sortField === 'company_address' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('snov_id')}>
                    Snov ID
                    {sortField === 'snov_id' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Deliverable" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('deliverable')}>
                      Del
                      {sortField === 'deliverable' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Risky" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('risky')}>
                      Ris
                      {sortField === 'risky' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Undeliverable" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('undeliverable')}>
                      Und
                      {sortField === 'undeliverable' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Bounce" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('bounce')}>
                      Bou
                      {sortField === 'bounce' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Linkedin')}>
                    LI
                    {sortField === 'Linkedin' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('employees_onlinkedin')}>
                    Employees on LinkedIn
                    {sortField === 'employees_onlinkedin' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('linkedin_id')}>
                    LinkedIn ID
                    {sortField === 'linkedin_id' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('twitter')}>
                    TW
                    {sortField === 'twitter' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Facebook')}>
                    FB
                    {sortField === 'Facebook' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ig')}>
                    IG
                    {sortField === 'ig' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('Youtube')}>
                    YT
                    {sortField === 'Youtube' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('license')}>
                    License
                    {sortField === 'license' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('phone')}>
                    Phone
                    {sortField === 'phone' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Unknown" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unknown')}>
                      Unk
                      {sortField === 'unknown' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 50, width: 50, fontWeight: 'bold', backgroundColor: '#fff', px: '4px !important' }}>
                  <Tooltip title="Not Verified" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('not_verified')}>
                      NV
                      {sortField === 'not_verified' && (
                        <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('suspicious')}>
                    Suspicious
                    {sortField === 'suspicious' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                    Status
                    {sortField === 'status' && (
                      <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDomains
                .slice(0, displayCount)
                .map((domain: any) => (
                  <TableRow key={domain.id} hover role="checkbox" tabIndex={-1}>
                    <TableCell>
                      <Checkbox
                        checked={selectedDomains.has(domain.id)}
                        onChange={() => handleSelectDomain(domain.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {!domain.exists_in_db ? (
                        (() => {
                          // Get tags in order: industry_tag, industry_tag_two, industry_tag_three, industry_tag_four
                          const selectedTags: { id: number; name: string }[] = [];
                          const safeIndustryTags = Array.isArray(industryTags) ? industryTags : [];
                          if (domain.industry_tag) {
                            const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag));
                            if (tag) selectedTags.push(tag);
                          }
                          if (domain.industry_tag_two) {
                            const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_two));
                            if (tag) selectedTags.push(tag);
                          }
                          if (domain.industry_tag_three) {
                            const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_three));
                            if (tag) selectedTags.push(tag);
                          }
                          if (domain.industry_tag_four) {
                            const tag = safeIndustryTags.find(t => Number(t.id) === Number(domain.industry_tag_four));
                            if (tag) selectedTags.push(tag);
                          }
                          const isMaxSelected = selectedTags.length >= 4;
                          const isOpen = openDropdowns.get(domain.id) || false;
                          
                          return (
                            <Autocomplete
                              multiple
                              options={safeIndustryTags}
                              value={selectedTags}
                              open={isMaxSelected ? false : isOpen}
                              onOpen={() => {
                                if (!isMaxSelected) {
                                  setOpenDropdowns(prev => new Map(prev).set(domain.id, true));
                                }
                              }}
                              onClose={() => {
                                setOpenDropdowns(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(domain.id, false);
                                  return newMap;
                                });
                              }}
                              onChange={(event, newValue) => {
                                // Limit to 4 tags
                                const limitedValue = newValue.slice(0, 4);
                                handleStatusChange(domain.id, limitedValue);
                                if (limitedValue.length >= 4) {
                                  setOpenDropdowns(prev => {
                                    const newMap = new Map(prev);
                                    newMap.set(domain.id, false);
                                    return newMap;
                                  });
                                }
                              }}
                              getOptionLabel={(option) => option?.name ?? ''}
                              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                              loading={safeIndustryTags.length === 0}
                              size="small"
                              limitTags={2}
                              sx={{ 
                                minWidth: 350,
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  fontSize: '0.75rem',
                                  minHeight: '30px',
                                  padding: '0 !important',
                                },
                                '& .MuiInputBase-input': {
                                  padding: '4px 8px !important',
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={params.inputProps.value ? '' : '-'}
                                  variant="outlined"
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: isMaxSelected,
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.75rem',
                                      minHeight: '30px',
                                    },
                                  }}
                                />
                              )}
                              renderTags={(value, getTagProps) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                      <Chip
                                        key={key}
                                        {...tagProps}
                                        label={option.name}
                                        size="small"
                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                      />
                                    );
                                  })}
                                  {isMaxSelected && (
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', alignSelf: 'center', ml: 0.5 }}>
                                      (max)
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              clearOnEscape
                              selectOnFocus
                              handleHomeEndKeys
                              noOptionsText={industryTags.length === 0 ? 'Loading tags...' : 'No matches'}
                            />
                          );
                        })()
                      ) : (
                        <span>{getConsolidatedTags(domain)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {!domain.exists_in_db ? (
                        <Autocomplete
                          value={domain.type_ || null}
                          onChange={(event, newValue) => {
                            handleTypeChange(domain.id, newValue || '');
                          }}
                          options={TYPE_OPTIONS}
                          size="small"
                          sx={{ 
                            minWidth: 150,
                            '& .MuiInputBase-root': {
                              fontSize: '0.75rem',
                              minHeight: '30px',
                              padding: '0 !important',
                            },
                            '& .MuiInputBase-input': {
                              padding: '4px 8px !important',
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="-"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '0.75rem',
                                  minHeight: '30px',
                                },
                              }}
                            />
                          )}
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                        />
                      ) : (
                        <span>{domain.type_ || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 250 }}>
                      {(() => {
                        const log = enrichLogs.find(l => l.domainId === domain.id);
                        const beforeCount = log ? (log.preCount ?? domain.pre_emails_count ?? 0) : (domain.pre_emails_count ?? 0);
                        
                        if (!log) {
                          const isAlreadyScraped = domain.last_ran_gmo !== null;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: isAlreadyScraped ? '#2e7d32' : '#9e9e9e' 
                              }} />
                              <Box>
                                <Typography variant="body2" sx={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: 600, 
                                  color: isAlreadyScraped ? 'success.main' : 'text.secondary' 
                                }}>
                                  {isAlreadyScraped ? 'Scraped' : 'Ready'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  {beforeCount} {isAlreadyScraped ? 'emails in DB' : 'existing emails in DB'}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }

                        if (log.status === 'pending') {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: '#1976d2',
                                animation: 'pulse 1.5s infinite ease-in-out',
                                '@keyframes pulse': {
                                  '0%': { opacity: 0.4 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.4 }
                                }
                              }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1976d2' }}>
                                  Queued
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  {beforeCount} existing emails in DB
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }

                        if (log.status === 'processing') {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'primary.main' }}>
                                  Scraping GMO...
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  Checking updates for {beforeCount} emails
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }

                        if (log.status === 'done') {
                          const isUpdated = (log.newCount ?? 0) > 0;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.2 }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'success.main' }}>
                                  {isUpdated ? 'Scraped (New Updates Found!)' : 'Scraped (No Updates)'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  {beforeCount} in DB before • <strong style={{ color: '#2e7d32' }}>+{log.newCount ?? 0} new updates</strong> • {beforeCount + (log.newCount ?? 0)} total now in DB
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }

                        if (log.status === 'failed') {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'error.main' }}>
                                  Scraper Failed
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  Keeping {beforeCount} existing in DB
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }

                        return null;
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{renderCompactLink(domain.website, 'website')}</TableCell>
                    <TableCell>{domain.name || '-'}</TableCell>
                    <TableCell>{domain.clean_name || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={String(domain.note || '')} arrow>
                        <span>{truncateText(String(domain.note || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={String(domain.description || '')} arrow>
                        <span>{truncateText(String(domain.description || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.unsubscribe ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.in_mailer ?? '-'}</TableCell>
                    <TableCell>{domain.state_name || '-'}</TableCell>
                    <TableCell>{domain.location || '-'}</TableCell>
                    <TableCell>{domain.country_name || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={String(domain.company_address || '')} arrow>
                        <span>{truncateText(String(domain.company_address || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{domain.snov_id || '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.deliverable ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.risky ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.undeliverable ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.bounce ?? '-'}</TableCell>
                    <TableCell>{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
                    <TableCell>{domain.employees_onlinkedin || '-'}</TableCell>
                    <TableCell>{domain.linkedin_id || '-'}</TableCell>
                    <TableCell>{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
                    <TableCell>{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
                    <TableCell>{renderCompactLink(domain.ig, 'ig')}</TableCell>
                    <TableCell>{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
                    <TableCell>{domain.license || '-'}</TableCell>
                    <TableCell>{domain.phone || '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.unknown ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ px: '4px !important' }}>{domain.not_verified ?? '-'}</TableCell>
                    <TableCell>{domain.suspicious || '-'}</TableCell>
                    <TableCell>{domain.status || '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Load More Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {Math.min(displayCount, domains.length)} of {domains.length}
        </Typography>
        
        {isLoadingMore ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : displayCount < domains.length ? (
          <button 
            onClick={handleLoadMore}
            style={{
              padding: '6px 16px',
              backgroundColor: 'transparent',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
              (e.target as HTMLButtonElement).style.color = 'white';
            }}
            onMouseOut={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.target as HTMLButtonElement).style.color = '#1976d2';
            }}
          >
            Load More
          </button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        )}
      </Box>

    </Box>
  );
};

export default DomainListPage;

